import {isCustomParameter, openDialog, Parameter, Tooltip} from 'platform/components';
import {Box, Hide, HStack, Link, Show, Text} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {Fragment} from 'react';

import {always, isNil, isNotNil, reject} from 'ramda';
import {isNilOrEmpty, isPositive} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {
  DOT_CHARACTER,
  EMPTY_PLACEHOLDER,
  getRandomId,
  isValidEmail,
  Nullish,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {useKonzultaSmsAvailability} from '../../hooks/useKonzultaSmsAvailability';
import {SendCustomerEmailForm} from '../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../SendCustomerSmsForm/SendCustomerSmsForm';

interface ActionParametersProps extends TestIdProps {
  parameters: Parameter[] | Nullish;
  color?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'white';
  size?: 'base' | 'small' | 'xSmall';
  customerId?: string;
  tooltipLabelSms?: string;
  tooltipLabelEmail?: string;
  resourceId?: EntityResourceIds;
  recordId?: string;
  vehicleId?: string | Nullish;
}

export function ActionParameters(props: ActionParametersProps) {
  const size = props.size ?? 'xSmall';
  const {isSmsActionDisabled: isSmsServiceUnavailable, smsTooltipLabel} =
    useKonzultaSmsAvailability();
  const filteredParameters = reject(isNilOrEmpty, props.parameters ?? []);
  if (!filteredParameters?.length) {
    return null;
  }

  return (
    <HStack spacing={1} data-testid={props['data-testid']} wrap align="center">
      {filteredParameters.map((parameter, index) => {
        const isCustom = isCustomParameter(parameter);
        const title = isCustom ? parameter.title : parameter;
        const content = isCustom ? parameter.content : undefined;
        const isTitleEmail = isNotNil(title) && isValidEmail(title);
        const isSmsActionDisabled = !isTitleEmail && isSmsServiceUnavailable;
        const tooltip = match([isCustom, isTitleEmail, isSmsActionDisabled])
          .with([Pattern.boolean, Pattern.boolean, true], always(smsTooltipLabel))
          .with(
            [true, Pattern.boolean, false],
            always(parameter && typeof parameter !== 'string' ? parameter.tooltip : '')
          )
          .with([Pattern.boolean, true, false], always(props.tooltipLabelEmail))
          .otherwise(always(props.tooltipLabelSms));

        const shouldShowAction =
          isNil(content) &&
          title !== EMPTY_PLACEHOLDER &&
          title !== i18n.t('entity.customer.labels.noEmail') &&
          title !== i18n.t('entity.customer.labels.noPhoneNumber');

        return (
          <Fragment key={`${parameter}_${getRandomId()}`}>
            <Show when={isPositive(index)}>
              <Text color="tertiary" size={size}>
                {DOT_CHARACTER}
              </Text>
            </Show>
            <Show when={shouldShowAction}>
              <Tooltip label={tooltip} placement="top" hasAutoWidth>
                <Link
                  data-testid={`${props['data-testid']}-sendLink-[${index.toString()}]`}
                  isDisabled={isSmsActionDisabled}
                  onClick={() =>
                    isTitleEmail
                      ? openDialog(
                          <SendCustomerEmailForm
                            customerId={props.customerId}
                            recordId={props.recordId}
                            resourceId={props.resourceId}
                            vehicleId={props.vehicleId}
                          />,
                          {
                            withAdditionalFooter: true,
                            size: 'large',
                            title: i18n.t('general.actions.sendEmail'),
                          }
                        )
                      : openDialog(
                          <SendCustomerSmsForm
                            data-testid={`${props['data-testid']}-sendSmsModal`}
                            customerId={props.customerId}
                            recordId={props.recordId}
                            resourceId={props.resourceId}
                          />,
                          {
                            scrollBehavior: 'outside',
                            title: i18n.t('general.labels.smsMessage'),
                            size: 'large',
                          }
                        )
                  }
                  title={title}
                  size={size}
                />
              </Tooltip>
            </Show>
            <Show when={isNotNil(content)}>
              <Box data-testid={suffixTestId(`parameter-[${index}]`, props)}>{content}</Box>
            </Show>
            <Hide when={shouldShowAction || isNotNil(content)}>
              <Text
                data-testid={suffixTestId(`parameter-[${index}]`, props)}
                color={props.color}
                size={size}
                noWrap
              >
                {title}
              </Text>
            </Hide>
          </Fragment>
        );
      })}
    </HStack>
  );
}
